<script setup>

import {computed, inject, ref, watch} from "vue";
import { useI18n } from "vue-i18n";
import { DateTime, Settings } from 'luxon'
import { useCommunitiesStore } from "@/stores/communitiesStore";
import { useCommunityStore } from "@/stores/communityStore";
import PodlistDiffTable from "@/components/PodlistDiffTable.vue";

// EDA dates are Austrian; render the Vienna calendar day.
const fmtDate = (v) => {
  if (!v) return '';
  const dt = DateTime.fromISO(String(v)).setZone('Europe/Vienna');
  return dt.isValid ? dt.toFormat('dd.MM.yyyy') : '';
};

const $log = inject('log');
const { t } = useI18n();
Settings.defaultZone = 'Europe/Vienna';

const communitiesStore = useCommunitiesStore();
const communityStore = useCommunityStore();

const communityAvailable = computed(() => {
  return communitiesStore.allCommunities.map(c => ({
    title: c.name,
    props: { subtitle: c.ecid },
    ecn: c.ecn,
    ecid: c.ecid,
    community: c,
  }));
});

const selectedCommunity = ref(null);
const messages = ref([]);
const messagesLoading = ref(false);
const messagesFailed = ref(false);

const selectedMessage = ref(null);
const diffResult = ref(null);
const diffLoading = ref(false);
const diffFailed = ref(false);

const messageHeaders = [
  { title: t('colArrivalDate'), key: 'arrivalDate' },
  { title: t('colProcessDate'), key: 'processDate' },
  { title: t('colMpCount'), key: 'mpCount' },
  { title: t('colMessageId'), key: 'messageId' },
];

watch(selectedCommunity, async (entry) => {
  selectedMessage.value = null;
  diffResult.value = null;
  messages.value = [];
  if (!entry?.ecid) return;

  messagesLoading.value = true;
  messagesFailed.value = false;
  try {
    messages.value = await communityStore.fetchPodlistMessages(entry.ecn, entry.ecid);
  } catch (e) {
    $log.error(e);
    messagesFailed.value = true;
  }
  messagesLoading.value = false;
});

async function selectMessage(_event, { item }) {
  selectedMessage.value = item;
  diffResult.value = null;
  if (!item?.messageId) return;

  diffLoading.value = true;
  diffFailed.value = false;
  try {
    diffResult.value = await communityStore.fetchPodlistDiff(
      selectedCommunity.value.ecn, selectedCommunity.value.ecid, item.messageId);
  } catch (e) {
    $log.error(e);
    diffFailed.value = true;
  }
  diffLoading.value = false;
}

</script>
<script>
export default {
  name: 'ViewCommunityPodlistViewer',
}
</script>


<template>
  <v-container class="containerBase pa-0">
    <v-container class="containerStandard">

      <p class="textHeadline">{{ $t('podlistViewer') }}</p>

      <v-combobox
        max-width="400px"
        bg-color="white"
        :class="{'mr-0': $vuetify.display.xs}"
        :placeholder="t('community')"
        v-model="selectedCommunity"
        :items="communityAvailable"
        :filter-keys="['title', 'value']"
        density="comfortable"
        variant="outlined"
        item-value="ecid"
        rounded="2"
        hide-details
        clearable
      ></v-combobox>

      <v-row v-if="messagesLoading" class="my-4">
        <v-progress-circular class="mx-auto" color="blue-lighten-3" indeterminate></v-progress-circular>
      </v-row>
      <div v-if="messagesFailed" class="my-4">{{ $t('loadFailed') }}</div>

      <template v-if="selectedCommunity && !messagesLoading">
        <p class="textHeadline mt-6">{{ $t('podlistMessages') }}</p>
        <v-data-table
          :headers="messageHeaders"
          :items="messages"
          :no-data-text="$t('noMessages')"
          density="comfortable"
          @click:row="selectMessage"
        >
          <template #item.arrivalDate="{ item }">{{ fmtDate(item.arrivalDate) }}</template>
          <template #item.processDate="{ item }">{{ fmtDate(item.processDate) }}</template>
        </v-data-table>
      </template>

      <v-row v-if="diffLoading" class="my-4">
        <v-progress-circular class="mx-auto" color="blue-lighten-3" indeterminate></v-progress-circular>
      </v-row>
      <div v-if="diffFailed" class="my-4">{{ $t('loadFailed') }}</div>

      <PodlistDiffTable v-if="diffResult && !diffLoading" :diff-result="diffResult" class="mt-6" />

    </v-container>
  </v-container>
</template>

<style>


</style>
